import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Grid, Typography, Select, MenuItem, Card, CardContent, FormControl, InputLabel } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { fetchOverviewAnalytics } from '../../api/analyticsApi'; // Add this import

const StatCard = ({ title, value, change, summary }) => {
  const isPositive = typeof change === 'string'
    ? !change.startsWith('–') && !change.includes('-')
    : change >= 0;
  const arrow = typeof change === 'string'
    ? (change === 'New' || isPositive ? '▲' : '▼')
    : (isPositive ? '▲' : '▼');
  const color = isPositive ? 'success.main' : 'error.main';

  return (
    <Box sx={{ px: 1 }}>
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          p: 2,
          boxShadow: 'none',
          border: 'none',
          minWidth: 0,
          textAlign: 'left'
        }}
      >
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 0.25, lineHeight: 1.1 }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color,
            fontWeight: 600,
            mb: summary ? 0.25 : 0,
            display: 'block'
          }}
        >
          {arrow} {typeof change === 'number' ? Math.abs(change) + '%' : change}
        </Typography>
        {summary && (
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mt={0.5}
            sx={{ lineHeight: 1.2 }}
          >
            {summary}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

const AnalyticsOverview = () => {
  const [period, setPeriod] = useState(() => localStorage.getItem('analyticsOverviewPeriod') || '7d');
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({
    users: 0, newUsers: 0, engagement: 0, revenue: '$0', changeUsers: 0, changeNewUsers: 0, changeEngagement: 0, changeRevenue: 0,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const propertyId = user?.propertyId?._id;
        const data = await fetchOverviewAnalytics({ propertyId, period });
        console.log('📊 Overview Data:', data);
        const numDays = parseInt(period.replace('d', ''), 10);
        const start = dayjs().subtract(numDays - 1, 'day');
        const paddedChart = Array.from({ length: numDays }).map((_, idx) => {
          const date = start.add(idx, 'day');
          const found = (data.chart || []).find(item =>
            item._id?.year === date.year() &&
            item._id?.month === date.month() + 1 &&
            item._id?.day === date.date()
          );
          return {
            date: date.format('MMM D'),
            current: Number(found?.current ?? found?.count ?? 0),
            previous: Number(found?.previous ?? 0),
          };
        });
        setChartData(paddedChart);
        setStats(data.stats || {});
      } catch (err) {
        console.error('Failed to fetch analytics overview', err);
      }
    };
    loadData();
    const interval = setInterval(loadData, 60000); // refresh every 60 seconds
    return () => clearInterval(interval);
  }, [period]);

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(60,64,67,0.15)',
        p: 3,
      }}
    >
      <Box display="flex" gap={2} mb={2}>
        <StatCard
          title="Users"
          value={stats.users}
          change={stats.changeUsers || '–'}
          summary={`Compared to previous ${period.replace('d', ' days')}`}
        />
        <StatCard
          title="New users"
          value={stats.newUsers}
          change={stats.changeNewUsers || '–'}
          summary={`Compared to previous ${period.replace('d', ' days')}`}
        />
        <StatCard
          title="Avg. engagement time"
          value={
            typeof stats.engagement === 'string'
              ? stats.engagement
              : isNaN(stats.engagement)
              ? '0m 0s'
              : formatDuration(Number(stats.engagement))
          }
          change={stats.changeEngagement || '–'}
          summary={`Compared to previous ${period.replace('d', ' days')}`}
        />
      </Box>

      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={({ x, y, payload }) => {
              const [month, day] = payload.value.split(' ');
              return (
                <g transform={`translate(${x},${y})`}>
                  <text x={0} y={0} dy={12} textAnchor="middle" fill="#666" fontSize={11}>
                    {day}
                  </text>
                  <text x={0} y={0} dy={24} textAnchor="middle" fill="#aaa" fontSize={10}>
                    {month}
                  </text>
                </g>
              );
            }}
            interval={0}
          />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey="current" stroke="#1976d2" strokeWidth={2} name="Last 7 days" />
          <Line type="monotone" dataKey="previous" stroke="#90caf9" strokeDasharray="5 5" name="Preceding period" />
        </LineChart>
      </ResponsiveContainer>

      <Box mt={2} textAlign="left">
        <FormControl variant="standard">
          <InputLabel id="period-label">Period</InputLabel>
          <Select
            labelId="period-label"
            value={period}
            onChange={(e) => {
              const value = e.target.value;
              setPeriod(value);
              localStorage.setItem('analyticsOverviewPeriod', value);
            }}
            disableUnderline
          >
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="60d">Last 60 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default AnalyticsOverview;
